/**
 * "Continue your work": turns dashboard navigations into recent-page visits.
 *
 * `resolveRecentPage` maps a route to what the backend needs (kind + key, plus the
 * route to reopen); entity pages send their id and the backend resolves the name,
 * static pages send the title the router gives them. `trackRouteVisit` is the
 * router hook: it never throws and never blocks navigation.
 */
import type { RouteLocationNormalized } from 'vue-router'
import { AuthService } from '@/services/auth.ts'
import { RecentPagesApi, type RecentPageKind, type RecentPageVisit } from '@/services/api/recent-pages.ts'

// Static pages worth coming back to. Keyed by route name, titled as the
// navigation names them. Home itself, auth and the empty placeholders are left out.
const STATIC_PAGES: Record<string, string> = {
  'Signals': 'Signals',
  'ReviQ Competitors': 'ReviQ Competitors',
  'Engagement Manager': 'Engagement Manager',
  'Data Export': 'Data Export',
  'FakeIntegrations': 'Integrations',
  'HubSpot Integration': 'HubSpot Integration',
  'Creatio Integration': 'Creatio Integration',
  'MCP Integration': 'MCP Integration',
  'Audience-Targeting Methodology': 'Audience-Targeting Methodology',
  'Account Engagement': 'Account Engagement',
  'Account Settings': 'Account Settings',
  'Team Management': 'Team Management',
  'Integrations': 'Integrations Settings',
  'Activity Log': 'Activity Log',
}

// Entity pages: route name -> kind. The id comes from the route params.
const ENTITY_ROUTES: Record<string, RecentPageKind> = {
  'Customer': 'customer',
  'Account': 'account',
  'Audiences': 'audience',
  'Audiences V1': 'audience',
  'Audiences Intersection': 'audience',
  'Categories': 'category',
  'Competitor': 'competitor',
}

function param(route: RouteLocationNormalized, name: string): string {
  const value = route.params[name]
  const raw = Array.isArray(value) ? value[0] : value
  return typeof raw === 'string' ? raw.trim() : ''
}

function withoutTrailingSlash(path: string): string {
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path
}

/** The visit a route represents, or null when the page isn't worth remembering. */
export function resolveRecentPage(route: RouteLocationNormalized): RecentPageVisit | null {
  const name = typeof route.name === 'string' ? route.name : ''
  const path = route.path

  const kind = ENTITY_ROUTES[name]
  if (kind) {
    const id = param(route, 'id')
    if (!id) return null
    return { kind, key: id, path: withoutTrailingSlash(path) }
  }

  if (name === 'Entity Card') {
    const entity = param(route, 'entity')
    const id = param(route, 'id')
    if (!id || (entity !== 'branch' && entity !== 'relationship_manager')) return null
    return { kind: entity, key: id, path: withoutTrailingSlash(path) }
  }

  if (name === 'Revio Insight Agent') {
    const conversationId = param(route, 'conversationId')
    if (conversationId) {
      return { kind: 'agent_conversation', key: conversationId, path: withoutTrailingSlash(path) }
    }
    return { kind: 'page', key: '/dashboard/agent', path: '/dashboard/agent', title: 'Revio Insight Agent' }
  }

  const title = STATIC_PAGES[name]
  if (title) {
    const key = withoutTrailingSlash(path)
    return { kind: 'page', key, path: key, title }
  }

  return null
}

/** Router `afterEach` hook. Reports the visit when the user is signed in. */
export function trackRouteVisit(to: RouteLocationNormalized): void {
  try {
    if (!AuthService.isAuthenticated()) return
    const visit = resolveRecentPage(to)
    if (visit) RecentPagesApi.visit(visit)
  } catch {
    // never let tracking interfere with navigation
  }
}
